import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  ClipboardList, Plus, CheckCircle2, Clock, AlertCircle, 
  Loader2, Trash2, Calendar, User, MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';
import { Avatar, AvatarFallback } from '../components/ui/avatar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TASK_CATEGORIES = [
  { value: 'venue', label: 'Venue & Location' },
  { value: 'catering', label: 'Food & Catering' },
  { value: 'equipment', label: 'Sound & Equipment' },
  { value: 'decoration', label: 'Decoration & Setup' },
  { value: 'transportation', label: 'Transportation' },
  { value: 'general', label: 'General' }
];

const Tasks = () => {
  const { user, isOrganizer } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [events, setEvents] = useState([]);
  const [attendees, setAttendees] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [filter, setFilter] = useState('all');
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    category: 'general',
    due_date: '',
    assigned_to: ''
  });

  const canManage = isOrganizer && isOrganizer();

  useEffect(() => {
    if (canManage) {
      fetchEvents();
    } else {
      if (user?.event_id) {
        setSelectedEventId(user.event_id);
      }
    }
  }, [user]);

  useEffect(() => {
    if (selectedEventId) {
      fetchTasks();
      if (canManage) {
        fetchAttendees();
      }
    }
  }, [selectedEventId, filter]);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/events/my`);
      setEvents(response.data);
      if (response.data.length > 0) {
        setSelectedEventId(response.data[0].id);
      } else {
        setLoading(false);
      }
    } catch (error) {
      toast.error('Failed to load events');
      setLoading(false);
    }
  };

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/event/tasks?event_id=${selectedEventId}`);
      setTasks(response.data);
    } catch (error) {
      console.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendees = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/event/attendees`);
      setAttendees(response.data);
    } catch (error) {
      console.error('Failed to load attendees');
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/event/tasks?event_id=${selectedEventId}`, {
        ...newTask,
        assigned_to: newTask.assigned_to || null
      });
      toast.success('Task created');
      setNewTask({ title: '', description: '', category: 'general', due_date: '', assigned_to: '' });
      setCreateDialogOpen(false);
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create task');
    }
  };

  const handleToggleStatus = async (taskId, currentStatus) => {
    const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
    try {
      await axios.put(`${API_URL}/api/event/tasks/${taskId}/status?status=${newStatus}`);
      fetchTasks();
      toast.success(newStatus === 'completed' ? 'Task completed!' : 'Task reopened');
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await axios.delete(`${API_URL}/api/event/tasks/${taskId}`);
      toast.success('Task deleted');
      fetchTasks();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const filteredTasks = tasks.filter(task => {
    if (filter === 'all') return true;
    if (filter === 'pending') return task.status === 'pending';
    if (filter === 'completed') return task.status === 'completed';
    return task.category === filter;
  });

  const pendingCount = tasks.filter(t => t.status === 'pending').length;
  const completedCount = tasks.filter(t => t.status === 'completed').length;

  const getCategoryLabel = (value) => {
    return TASK_CATEGORIES.find(c => c.value === value)?.label || value;
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-PH', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAssigneeName = (userId) => {
    const attendee = attendees.find(a => a.id === userId);
    return attendee ? `${attendee.first_name} ${attendee.last_name}` : 'Unknown';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-maroon" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="tasks-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-maroon flex items-center gap-3">
            <ClipboardList className="h-8 w-8" />
            Task Checklist
          </h1>
          <p className="text-gray-600 mt-1">
            Logistics & event preparation tasks
          </p>
        </div>

        <div className="flex items-center gap-3">
          {canManage && events.length > 1 && (
            <Select value={selectedEventId} onValueChange={setSelectedEventId}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Select Event" />
              </SelectTrigger>
              <SelectContent>
                {events.map(event => (
                  <SelectItem key={event.id} value={event.id}>
                    {event.event_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-maroon hover:bg-maroon-hover text-white rounded-full" data-testid="create-task-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Task
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading text-maroon">Create Task</DialogTitle>
                <DialogDescription>Add a new task to the checklist</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateTask} className="space-y-4">
                <div className="space-y-2">
                  <Label>Task Title</Label>
                  <Input
                    value={newTask.title}
                    onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                    placeholder="e.g., Confirm venue booking"
                    required
                    data-testid="task-title-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select value={newTask.category} onValueChange={(v) => setNewTask({ ...newTask, category: v })}>
                      <SelectTrigger data-testid="task-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TASK_CATEGORIES.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Due Date</Label>
                    <Input
                      type="date"
                      value={newTask.due_date}
                      onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                      data-testid="task-due-date"
                    />
                  </div>
                </div>
                {canManage && (
                  <div className="space-y-2">
                    <Label>Assign To</Label>
                    <Select value={newTask.assigned_to || "unassigned"} onValueChange={(v) => setNewTask({ ...newTask, assigned_to: v === "unassigned" ? '' : v })}>
                      <SelectTrigger data-testid="task-assignee-select">
                        <SelectValue placeholder="Select attendee (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {attendees.map(a => (
                          <SelectItem key={a.id} value={a.id}>{a.first_name} {a.last_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={newTask.description}
                    onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                    placeholder="Task details..."
                    data-testid="task-description-input"
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-maroon hover:bg-maroon-hover text-white" data-testid="submit-task-btn">
                    Create Task
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm bg-white">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <ClipboardList className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{tasks.length}</p>
              <p className="text-sm text-gray-500">Total Tasks</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-white">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Clock className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{pendingCount}</p>
              <p className="text-sm text-gray-500">Pending</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-white">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{completedCount}</p>
              <p className="text-sm text-gray-500">Completed</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={filter === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('all')}
          className={filter === 'all' ? 'bg-maroon hover:bg-maroon-hover' : ''}
        >
          All
        </Button>
        <Button
          variant={filter === 'pending' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('pending')}
          className={filter === 'pending' ? 'bg-amber-500 hover:bg-amber-600' : ''}
        >
          Pending
        </Button>
        <Button
          variant={filter === 'completed' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('completed')}
          className={filter === 'completed' ? 'bg-green-500 hover:bg-green-600' : ''}
        >
          Completed
        </Button>
        {TASK_CATEGORIES.map(cat => (
          <Button
            key={cat.value}
            variant={filter === cat.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(cat.value)}
            className={filter === cat.value ? 'bg-blue-500 hover:bg-blue-600' : ''}
          >
            {cat.label}
          </Button>
        ))}
      </div>

      {/* Task List */}
      {filteredTasks.length === 0 ? (
        <Card className="border-0 shadow-sm bg-white">
          <CardContent className="p-12 text-center">
            <ClipboardList className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-600">No tasks yet</h3>
            <p className="text-gray-400 mt-1">Create your first task to get started</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredTasks.map((task) => {
            const hasReports = task.reports && task.reports.length > 0;
            
            return (
            <Card 
              key={task.id}
              className={`border-0 shadow-sm transition-all ${
                task.status === 'completed' ? 'bg-green-50' : 'bg-white hover:shadow-md'
              }`}
              data-testid={`task-card-${task.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <Checkbox
                    checked={task.status === 'completed'}
                    onCheckedChange={() => handleToggleStatus(task.id, task.status)}
                    className="mt-1 h-5 w-5"
                    data-testid={`task-checkbox-${task.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <Badge variant="outline" className="text-xs">
                        {getCategoryLabel(task.category)}
                      </Badge>
                      {task.status === 'completed' && (
                        <Badge className="bg-green-100 text-green-700 text-xs">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Done
                        </Badge>
                      )}
                      {hasReports && (
                        <Badge variant="outline" className="text-xs text-blue-600 border-blue-200">
                          <MessageSquare className="h-3 w-3 mr-1" />
                          {task.reports.length} report{task.reports.length > 1 ? 's' : ''}
                        </Badge>
                      )}
                    </div>
                    <h3 className={`font-semibold ${task.status === 'completed' ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                      {task.title}
                    </h3>
                    {task.description && (
                      <p className="text-sm text-gray-500 mt-1">{task.description}</p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                      {task.due_date && (
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(task.due_date).toLocaleDateString()}
                        </span>
                      )}
                      {task.assigned_to && (
                        <span className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {getAssigneeName(task.assigned_to)}
                        </span>
                      )}
                    </div>

                    {/* Reports Section */}
                    {hasReports && (
                      <div className="mt-4 border-t pt-4 space-y-3">
                        <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                          <MessageSquare className="h-4 w-4" />
                          Task Reports from Attendee
                        </h4>
                        {task.reports.map((report, idx) => (
                          <div 
                            key={report.id || idx}
                            className="bg-blue-50 rounded-lg p-3 border border-blue-100"
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <Avatar className="h-6 w-6">
                                <AvatarFallback className="bg-maroon text-white text-xs">
                                  {getInitials(report.user_name)}
                                </AvatarFallback>
                              </Avatar>
                              <span className="font-medium text-sm text-gray-900">
                                {report.user_name}
                              </span>
                              <span className="text-xs text-gray-400">
                                {formatDate(report.created_at)}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700 pl-8">{report.content}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteTask(task.id)}
                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                    data-testid={`delete-task-${task.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )})}
        </div>
      )}
    </div>
  );
};

export default Tasks;
